import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, ShieldCheck, RefreshCw } from 'lucide-react'
import { useAuthStore, useCartStore } from '../store/index.js'
import { authService, cartService } from '../services/api.js'
import toast from 'react-hot-toast'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from || '/'
  const login = useAuthStore(s => s.login)
  const mergeServerCart = useCartStore(s => s.mergeServerCart)

  // Step 1 — credentials
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw]     = useState(false)

  // Step 2 — 2FA (totp or email)
  const [step, setStep]           = useState(1)
  const [twoFaMethod, setTwoFaMethod] = useState(null) // 'totp' | 'email'
  const [emailHint, setEmailHint] = useState('')
  const [otpCode, setOtpCode]     = useState('')
  const [resending, setResending] = useState(false)

  const [loading, setLoading] = useState(false)

  const finishLogin = async (access_token) => {
    const tempStore = JSON.parse(localStorage.getItem('tonys-auth') || '{}')
    localStorage.setItem('tonys-auth', JSON.stringify({
      ...tempStore,
      state: { ...tempStore.state, token: access_token }
    }))
    const userData = await authService.me()
    login(userData, access_token)
    try {
      const serverCart = await cartService.get()
      const serverItems = serverCart?.items || serverCart || []
      if (serverItems.length) mergeServerCart(serverItems)
    } catch { /* non-fatal */ }
    toast.success('Welcome back!')
    navigate(from, { replace: true })
  }

  // ── Step 1: credentials ──────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const tokenData = await authService.login(email, password)
      if (tokenData.requires_2fa) {
        const method = tokenData.two_fa_method || 'totp'
        setTwoFaMethod(method)
        setEmailHint(tokenData.email_hint || '')
        setStep(2)
        setOtpCode('')
        if (method === 'email') {
          toast.success('A verification code has been sent to your email.')
        }
      } else {
        await finishLogin(tokenData.access_token)
      }
    } catch (err) {
      toast.error(err.message || 'Invalid credentials')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2a: TOTP verify ─────────────────────────────────────
  const handleTotp = async (e) => {
    e.preventDefault()
    const clean = otpCode.replace(/\s/g, '')
    if (clean.length !== 6) { toast.error('Enter the 6-digit code'); return }
    setLoading(true)
    try {
      const res = await authService.login2fa(email, password, clean)
      await finishLogin(res.access_token)
    } catch (err) {
      toast.error(err.message || 'Invalid authenticator code')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2b: Email OTP verify ────────────────────────────────
  const handleEmailOtp = async (e) => {
    e.preventDefault()
    const clean = otpCode.replace(/\s/g, '')
    if (clean.length !== 6) { toast.error('Enter the 6-digit code'); return }
    setLoading(true)
    try {
      const res = await authService.loginEmailOtpVerify(email, password, clean)
      await finishLogin(res.access_token)
    } catch (err) {
      toast.error(err.message || 'Invalid or expired code')
    } finally {
      setLoading(false)
    }
  }

  // ── Resend email OTP ─────────────────────────────────────────
  const handleResend = async () => {
    setResending(true)
    try {
      await authService.loginEmailOtpSend(email, password)
      toast.success('A new code has been sent to your email.')
    } catch (err) {
      toast.error(err.message || 'Failed to resend code')
    } finally {
      setResending(false)
    }
  }

  const Spinner = () => (
    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
  )

  return (
    <div className="min-h-screen bg-surface-dark flex flex-col safe-top safe-bottom">
      <div className="flex-1 px-6 pt-4 pb-8">
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo.text-w.png"
            alt="Tony's Liquor"
            className="h-12 w-auto object-contain mb-5"
          />
          <h1 className="font-heading text-white text-2xl">Welcome Back</h1>
          <p className="font-display text-gray-400 text-sm mt-1">Sign in to your account</p>
        </div>

        {/* ── Step 1: Credentials ── */}
        {step === 1 && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm font-display mb-1.5">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"/>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" className="input-field pl-10" required/>
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-sm font-display mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"/>
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" className="input-field pl-10 pr-10" required/>
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500">
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-primary text-sm font-display">Forgot Password?</Link>
            </div>
            <button type="submit" disabled={loading}
              className="btn-primary w-full py-4 text-base mt-2 flex items-center justify-center gap-2">
              {loading ? <Spinner/> : 'Sign In'}
            </button>
          </form>
        )}

        {/* ── Step 2: 2FA ── */}
        {step === 2 && (() => {
          const isEmail = twoFaMethod === 'email'
          return (
            <form onSubmit={isEmail ? handleEmailOtp : handleTotp} className="space-y-5">
              <div className="flex flex-col items-center text-center py-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                  {isEmail
                    ? <Mail size={28} className="text-primary"/>
                    : <ShieldCheck size={28} className="text-primary"/>}
                </div>
                <h2 className="font-heading text-white text-xl">Two-Factor Verification</h2>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed max-w-xs">
                  {isEmail
                    ? <>A 6-digit code was sent to{' '}
                        <strong className="text-gray-300">{emailHint || 'your email'}</strong>.
                        Enter it below.</>
                    : 'Open your authenticator app and enter the 6-digit code.'}
                </p>
              </div>

              <div>
                <label className="block text-gray-400 text-sm font-display mb-1.5">
                  {isEmail ? 'Email Code' : 'Authenticator Code'}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="input-field text-center text-3xl tracking-[0.5em] font-mono py-4"
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              <button type="submit" disabled={loading || otpCode.length < 6}
                className="btn-primary w-full py-4 text-base flex items-center justify-center gap-2">
                {loading
                  ? <Spinner/>
                  : <><ShieldCheck size={18}/> Verify &amp; Sign In</>}
              </button>

              {isEmail && (
                <button type="button" onClick={handleResend} disabled={resending}
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors py-1">
                  <RefreshCw size={13} className={resending ? 'animate-spin' : ''}/>
                  {resending ? 'Sending…' : "Didn't receive it? Resend code"}
                </button>
              )}

              <button type="button" onClick={() => { setStep(1); setOtpCode(''); setTwoFaMethod(null) }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-300 transition-colors py-2">
                ← Back to login
              </button>
            </form>
          )
        })()}

        {step === 1 && (
          <p className="text-center text-gray-500 text-xs font-display mt-8">
            Account access is by invitation only.{' '}
            <a href="mailto:orders@tonysliquor.co.za" className="text-primary">Contact us</a> to request access.
          </p>
        )}
      </div>
    </div>
  )
}
